/**
 * `src/lib/audio/player.ts` — one audio element, one queue, one surah.
 *
 * SPEC §1.3 / §2.2: recitation is fetched from everyayah.com ON AN EXPLICIT
 * TAP ONLY. Nothing here runs until `play()` is called from the user's own
 * click; there is no autoplay, no prefetch on opening a surah and nothing on
 * install.
 *
 * HOW CACHING WORKS (and why the element is pointed at the real URL)
 * ──────────────────────────────────────────────────────────────────
 * The element's `src` is the everyayah URL itself, set SYNCHRONOUSLY inside
 * the tap, because iOS Safari only lets a media element start inside a user
 * gesture — an `await` before `play()` loses it. A media element asks with a
 * Range header and gets a 206, which the service worker must not cache
 * (`CacheableResponsePlugin([200])`, SPEC §1.3). So beside it the player
 * issues one ordinary full `fetch()` for the CURRENT ayah and the NEXT one —
 * the preload — which the service worker's CacheFirst route stores as a 200.
 * From then on the element's Range requests for those ayat are answered from
 * the cache by `RangeRequestsPlugin`, offline included. `mode: 'cors'`,
 * `credentials: 'omit'` and `crossOrigin = 'anonymous'` keep every response
 * a real CORS response rather than an opaque one.
 *
 * Every dependency is injected so the queue, the preload and the media-session
 * wiring can be tested without a browser.
 */

import { reciterOf } from './reciters';
import { firstTrack, nextTrack, previousTrack, sameTrack, trackUrl, type Track } from './queue';

/** The part of `HTMLAudioElement` the player uses. */
export interface AudioLike {
	src: string;
	crossOrigin: string | null;
	preload: string;
	currentTime: number;
	paused: boolean;
	play(): Promise<void> | void;
	pause(): void;
	removeAttribute(name: string): void;
	load(): void;
	addEventListener(type: string, listener: () => void): void;
}

/** The part of `navigator.mediaSession` the player uses. */
export interface MediaSessionLike {
	metadata: unknown;
	playbackState: 'none' | 'paused' | 'playing';
	setActionHandler(action: string, handler: (() => void) | null): void;
}

export interface PlayerDeps {
	createAudio(): AudioLike;
	/** the audited fetch wrapper */
	fetch(url: string, init: RequestInit): Promise<{ ok: boolean; blob(): Promise<unknown> }>;
	mediaSession?: MediaSessionLike | null;
	/** `new MediaMetadata(init)`, or anything carrying the same fields */
	metadata?: (init: { title: string; artist: string; album: string }) => unknown;
	/** the Arabic title of a track, for the lock screen */
	describe(track: Track): string;
	loadReciter(): Promise<unknown>;
	saveReciter(id: string): Promise<unknown>;
}

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

export interface PlayerState {
	track: Track | null;
	status: PlayerStatus;
	reciterId: string;
	/** an Arabic sentence when something went wrong, else `null` */
	error: string | null;
}

export interface Player {
	state(): PlayerState;
	subscribe(listener: (state: PlayerState) => void): () => void;
	/** Start at `surah:ayah`. Call it from the click handler itself. */
	play(surah: number, ayah: number): void;
	toggle(): void;
	next(): void;
	previous(): void;
	stop(): void;
	setReciter(id: string): void;
}

export const AUDIO_FAILED = 'تعذّر تشغيل التلاوة. تحقّق من الاتصال بالشبكة ثم أعد المحاولة.';

export function createPlayer(deps: PlayerDeps): Player {
	let audio: AudioLike | null = null;
	let state: PlayerState = {
		track: null,
		status: 'idle',
		reciterId: reciterOf(null).id,
		error: null
	};
	const listeners = new Set<(s: PlayerState) => void>();
	/** URLs already fully fetched (and so in the SW cache) this session. */
	const warmed = new Set<string>();

	void deps
		.loadReciter()
		.then((id) => {
			if (state.track === null) set({ reciterId: reciterOf(id).id });
		})
		.catch(() => {});

	function set(patch: Partial<PlayerState>): void {
		state = { ...state, ...patch };
		for (const listener of listeners) listener(state);
		syncSession();
	}

	function element(): AudioLike {
		if (audio) return audio;
		const el = deps.createAudio();
		el.crossOrigin = 'anonymous';
		el.preload = 'auto';
		el.addEventListener('playing', () => set({ status: 'playing', error: null }));
		el.addEventListener('pause', () => {
			if (state.track && state.status === 'playing') set({ status: 'paused' });
		});
		el.addEventListener('waiting', () => {
			if (state.track) set({ status: 'loading' });
		});
		el.addEventListener('ended', () => advance());
		el.addEventListener('error', () => {
			if (state.track) set({ status: 'error', error: AUDIO_FAILED });
		});
		audio = el;
		bindSession();
		return el;
	}

	/** Full-body fetch so the service worker stores a 200. Fire and forget. */
	function warm(track: Track | null): void {
		if (!track) return;
		const url = trackUrl(state.reciterId, track);
		if (warmed.has(url)) return;
		warmed.add(url);
		void deps
			.fetch(url, { mode: 'cors', credentials: 'omit' })
			.then((res) => (res.ok ? res.blob() : Promise.reject(new Error('not ok'))))
			.catch(() => warmed.delete(url));
	}

	/** Point the element at `track` and play. Synchronous up to `play()`. */
	function start(track: Track): void {
		const el = element();
		set({ track, status: 'loading', error: null });
		el.src = trackUrl(state.reciterId, track);
		const started = el.play();
		if (started && typeof (started as Promise<void>).catch === 'function') {
			(started as Promise<void>).catch(() => {
				/* A rejected play() is normal when a newer track replaced this one
				 * before it began; only report it if this is still the track. */
				if (sameTrack(state.track, track) && el.paused) {
					set({ status: 'error', error: AUDIO_FAILED });
				}
			});
		}
		warm(track);
		warm(nextTrack(track));
	}

	function advance(): void {
		const current = state.track;
		const following = current ? nextTrack(current) : null;
		if (following) start(following);
		else stop();
	}

	function stop(): void {
		if (audio) {
			audio.pause();
			audio.removeAttribute('src');
			audio.load();
		}
		set({ track: null, status: 'idle', error: null });
	}

	function syncSession(): void {
		const session = deps.mediaSession;
		if (!session) return;
		if (!state.track) {
			session.metadata = null;
			session.playbackState = 'none';
			return;
		}
		session.playbackState = state.status === 'paused' ? 'paused' : 'playing';
		if (deps.metadata) {
			session.metadata = deps.metadata({
				title: deps.describe(state.track),
				artist: reciterOf(state.reciterId).name,
				album: 'غُرَبَاء'
			});
		}
	}

	function bindSession(): void {
		const session = deps.mediaSession;
		if (!session) return;
		const handlers: [string, () => void][] = [
			['play', () => toggle()],
			['pause', () => toggle()],
			['previoustrack', () => previous()],
			['nexttrack', () => next()],
			['stop', () => stop()]
		];
		for (const [action, handler] of handlers) {
			try {
				session.setActionHandler(action, handler);
			} catch {
				/* An action this browser does not know is not an error. */
			}
		}
	}

	function toggle(): void {
		if (!state.track || !audio) return;
		if (audio.paused) {
			const resumed = audio.play();
			if (resumed && typeof (resumed as Promise<void>).catch === 'function') {
				(resumed as Promise<void>).catch(() => set({ status: 'error', error: AUDIO_FAILED }));
			}
		} else {
			audio.pause();
			set({ status: 'paused' });
		}
	}

	function next(): void {
		advance();
	}

	function previous(): void {
		if (!state.track) return;
		if (audio && audio.currentTime > 3 && !state.track.basmala) {
			/* Like every player: "back" a few seconds in restarts the ayah. */
			start(state.track);
			return;
		}
		start(previousTrack(state.track));
	}

	return {
		state: () => state,
		subscribe(listener) {
			listeners.add(listener);
			listener(state);
			return () => listeners.delete(listener);
		},
		play(surah, ayah) {
			start(firstTrack(surah, ayah));
		},
		toggle,
		next,
		previous,
		stop,
		setReciter(id) {
			const reciter = reciterOf(id);
			if (reciter.id === state.reciterId) return;
			set({ reciterId: reciter.id });
			void deps.saveReciter(reciter.id);
			/* Switching reciter mid-ayah restarts that ayah in the new voice. */
			if (state.track && state.status !== 'idle') start(state.track);
		}
	};
}
